import type { PricingBreakdown as Breakdown } from './computeOrderPricing';
import { REGION_LABELS } from './regions';
import { formatCents, formatPct } from './money';

export interface PricingBreakdownProps {
  breakdown: Breakdown;
  /** 'exact' shows the surcharge as fixed; 'clamped'/undefined softens the wording. */
  surchargeBasis?: 'exact' | 'clamped';
  /** Label shown to an admin that this is internal-only (customer can't see it). */
  internalOnly?: boolean;
}

const cell: React.CSSProperties = { padding: '4px 8px', whiteSpace: 'nowrap' };
const right: React.CSSProperties = { ...cell, textAlign: 'right' };

/**
 * Region-aware estimate breakdown, shown in the customer order form and the
 * admin manage-load modal. Pure display — the math lives in computeOrderPricing.
 */
export default function PricingBreakdown({
  breakdown,
  surchargeBasis,
  internalOnly,
}: PricingBreakdownProps) {
  const { priced, region, lines, grandTotalCents } = breakdown;

  // Flat fees (direct ship) are pulled out of the per-ton line items and shown
  // as their own totals lines below the subtotal.
  const itemLines = lines.filter((l) => !l.flat);
  const flatLines = lines.filter((l) => l.flat);
  const itemsSubtotalCents = itemLines.reduce((sum, l) => sum + l.lineTotalCents, 0);

  if (!priced) {
    return (
      <div className="card" style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <strong>Estimated price</strong>
          {internalOnly && <span className="muted tiny">(internal — not shown to customer)</span>}
        </div>
        <div className="muted tiny" style={{ marginTop: 6 }}>
          {region === 'OTHER'
            ? 'This region has no standard pricing — contact the office for a quote.'
            : 'Pricing isn’t configured for this product yet.'}
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <strong>Estimated price</strong>
        <span className="muted tiny">{REGION_LABELS[region]}</span>
        {internalOnly && (
          <span className="muted tiny" style={{ marginLeft: 'auto' }}>
            internal — not shown to customer
          </span>
        )}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: '#64748b', textAlign: 'left' }}>
              <th style={cell}>Line</th>
              <th style={right}>Per ton</th>
              <th style={right}>Subtotal</th>
              <th style={right}>Fuel surcharge</th>
              <th style={right}>Line total</th>
            </tr>
          </thead>
          <tbody>
            {itemLines.map((l) => (
              <tr key={l.key} style={{ borderTop: '1px solid #e2e8f0' }}>
                <td style={cell}>
                  {l.label}
                  {l.discountPerTonCents > 0 && (
                    <span className="muted tiny">
                      {' '}
                      ({formatCents(l.basePerTonCents)} − {formatCents(l.discountPerTonCents)} tier)
                    </span>
                  )}
                </td>
                <td style={right}>{formatCents(l.perTonCents)}</td>
                <td style={right}>{formatCents(l.subtotalCents)}</td>
                <td style={right}>
                  {formatCents(l.surchargeCents)}{' '}
                  <span className="muted tiny">({formatPct(l.surchargePct)})</span>
                </td>
                <td style={right}>{formatCents(l.lineTotalCents)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            {flatLines.length > 0 && (
              <>
                <tr style={{ borderTop: '2px solid #cbd5e1' }}>
                  <td style={cell} colSpan={4}>
                    Line items
                  </td>
                  <td style={right}>{formatCents(itemsSubtotalCents)}</td>
                </tr>
                {flatLines.map((l) => (
                  <tr key={l.key}>
                    <td style={cell} colSpan={4}>
                      {l.label}
                    </td>
                    <td style={right}>{formatCents(l.lineTotalCents)}</td>
                  </tr>
                ))}
              </>
            )}
            <tr
              style={{
                borderTop: flatLines.length > 0 ? '1px solid #e2e8f0' : '2px solid #cbd5e1',
                fontWeight: 600,
              }}
            >
              <td style={cell} colSpan={4}>
                Estimated total
              </td>
              <td style={right}>{formatCents(grandTotalCents)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <ul className="muted tiny" style={{ margin: '8px 0 0', paddingLeft: 18 }}>
        <li>
          Fuel surcharge {surchargeBasis === 'clamped' ? 'is estimated and ' : ''}varies by delivery
          date.
        </li>
        <li>Final price adjusts to the actual delivered tons.</li>
      </ul>
    </div>
  );
}
